<template>
    <div class="header__search">
        <transition name="fade">
            <button
                v-show="!isSearchActive"
                class="cibButton cibButton--empty open-btn"
                @click="toggleSearch"
            >
                <nuxt-img
                    src="search.svg"
                    class="header__search__icon"
                    alt=""
                />
            </button>
        </transition>
        <transition name="fade">
            <div
                v-show="isSearchActive"
                class="search-overlay"
                @click="close"
            />
        </transition>
        <transition name="fade">
            <div
                v-show="isSearchActive"
                :class="['header__search_container', {suggestions: allowRenderSuggestions}]"
            >
                <form
                    class="search-form"
                    @submit.prevent="searchHandler"
                >
                    <button
                        class="cibButton cibButton--empty search-btn mr-8"
                        type="submit"
                    >
                        <SearchSvg />
                    </button>
                    <input
                        id="search"
                        ref="searchRef"
                        autocomplete="off"
                        type="text"
                        name="search"
                        class="search-form__input"
                        placeholder="Поиск по сайту"
                        :maxlength="$CONSTANTS.MAX_SEARCH_QUERY_LENGTH"
                        :value="searchModel"
                        @input="onInputHandler"
                        @keypress.enter="searchHandler"
                    >
                    <button
                        class="cibButton cibButton--empty close-btn"
                        type="button"
                        @click="close"
                    >
                        <nuxt-img
                            src="icons/crosses/search-cross.svg"
                            alt=""
                        />
                    </button>
                </form>
                <transition name="fade">
                    <UISearchSuggestions
                        v-if="allowRenderSuggestions"
                        class="search-form__suggestions"
                        :suggestions="formattedSuggestions"
                        @change="onSuggestionPickHandler"
                    >
                        <template #suggestion="{suggestion}">
                            <span class="search-form__suggestions__sub-label">{{ suggestion.subLabel }}</span>
                        </template>
                    </UISearchSuggestions>
                </transition>
            </div>
        </transition>
    </div>
</template>

<script>
import {computed, defineComponent, nextTick, ref, shallowRef} from 'vue';
import {useContext, useRouter} from '@nuxtjs/composition-api';
import clone from 'lodash.clonedeep';
import debounce from 'lodash.debounce';
import sanitizeHtml from 'sanitize-html';

import {useSearchInMenu} from '@/core/composables/useSearchInMenu';
import {stripCombiningMarks} from '@/core/utils/stripCombiningMarks';
import SearchSvg from '@/assets/images/search.svg?inline';

export default defineComponent({
    name: 'HeaderSearch',
    components: {
        SearchSvg,
        UISearchSuggestions: () => import('@/components/UI-components/UISearch/UISearchSuggestions.vue'),
    },
    setup() {
        const {$ym} = useContext();
        const router = useRouter();

        const {menu, formattedSuggestions, allowRenderSuggestions, searchInMenu, resetSuggestions} = useSearchInMenu();

        const searchRef = shallowRef(null);
        const searchModel = ref('');
        const isSearchActive = ref(false);

        const pathToSearchPage = computed(() => {
            return `/search?search=${encodeURI(sanitizeHtml(stripCombiningMarks(searchModel.value)))}`;
        });

        function toggleSearch() {
            isSearchActive.value = !isSearchActive.value;
            if (isSearchActive.value) {
                nextTick().then(() => searchRef.value?.focus());
            }
        }

        function close() {
            isSearchActive.value = false;
            searchModel.value = '';
            resetSuggestions();
        }

        function onSuggestionPickHandler(suggestion) {
            $ym('sendSearch');
            router.push(suggestion.pathToPage);
            close();
        }

        function searchHandler() {
            if (!searchModel.value) {
                return;
            }

            $ym('sendSearch');
            router.push(pathToSearchPage.value);
            close();
        }

        const onInputHandler = debounce(function({target: {value}}) {
            searchModel.value = value.replace(/\s{2,}/g, ' ');

            if (searchModel.value.trim().length < 3) {
                resetSuggestions();

                return;
            }

            const payload = stripCombiningMarks(sanitizeHtml(searchModel.value));

            resetSuggestions();
            searchInMenu(payload, clone(menu));
            // const searchOnSite = {
            //     title: `Поиск по&nbsp;сайту <b>&quot;${payload}&quot;</b>`,
            //     pathToPage: pathToSearchPage.value,
            // };
            // suggestions.value = [...suggestions.value, searchOnSite];
        }, 400);

        return {
            searchRef,
            searchModel,
            isSearchActive,
            allowRenderSuggestions,
            formattedSuggestions,
            searchInMenu,
            onInputHandler,
            toggleSearch,
            onSuggestionPickHandler,
            searchHandler,
            close,
        };
    },
});
</script>

<style lang="scss">
@import '@/assets/styles/ui-elements/cibButton';
@import '@/assets/styles/common/header/header-search';
</style>
